<template>
  <div class="mx-3">
    <v-row>
      <v-col
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <setup-leaves-leave-entitlement-setting-dialog-create
          v-if="$hasPermissionTo(['create job bands'])" 
          @created="loadData"
        />
      </v-col>
      <v-spacer />

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-select
          v-model="statusFilter"
          :items="items"
          item-text="state"
          item-value="value"
          label="Leave Entitlement Setting's Status"
          placeholder="Active / Deleted"
        />
      </v-col>

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-text-field
          v-model="search"
          placeholder="Key in Leave Entitlement Setting name here"
          append-icon="search"
          label="Search Leave Entitlement Setting"
          hide-no-data
          hide-selected
          clearable 
        />
      </v-col>
    </v-row>
  
    <v-data-table
      :headers="headers"
      :items="filteredLeaveEntitlementSettings"
      :single-expand="true"
      show-expand
      :search="search"
      :loading="tableLoading"
    >
      <template v-slot:item="props">
        <tr>
          <td :class="props.isExpanded?'remove-bottom-border':'border-bottom'">
            <v-btn
              icon
              @click="props.expand(!props.isExpanded)"
            >
              <v-icon small> 
                {{ props.isExpanded ? 'fa-chevron-up':'fa-chevron-down' }} 
              </v-icon>
            </v-btn>
          </td>

          <td :class="props.isExpanded? 'remove-bottom-border':'border-bottom' ">
            <span class="font-weight-bold">
              {{ props.item.name }}
            </span>
          </td>

          <td :class="props.isExpanded? 'remove-bottom-border':'border-bottom' ">
            <span class="font-weight-bold">
              {{ ((props.item||{}).leave_type ||{}).name || 'N/A' }}
            </span>
          </td>

          <td :class="props.isExpanded? 'remove-bottom-border':'border-bottom' ">
            <span v-if="statusFilter">
              <span class="mx-1" />
              <setup-leaves-leave-entitlement-setting-dialog-edit
                v-if="$hasPermissionTo(['update leave entitlement settings'])"
                :setting="props.item"
                @updated="loadData"
              />
              <setup-leaves-leave-entitlement-setting-dialog-delete
                v-if="$hasPermissionTo(['delete leave entitlement settings'])"
                :setting="props.item"
                @deleted="loadData"
              />
            </span>
            <span v-else>
              <setup-leaves-leave-entitlement-setting-dialog-restore
                v-if="$hasPermissionTo(['restore leave entitlement settings'])"
                :setting="props.item"
                @restored="loadData"
              />
            </span>
          </td>
        </tr>
      </template>
            
      <template v-slot:expanded-item="{ headers, item }">   
        <td
          class="details pb-3"
          :colspan="12"
        >
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">
                    Start Day
                  </th>
                  <th class="text-left">
                    End Day
                  </th>
                  <th class="text-left">
                    Days
                  </th>
                  <th class="text-left">
                    Maximum Carry Forward Days
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(detail,index) in item.leave_entitlement_setting_details"
                  :key="index"
                >
                  <td>{{ detail.start_day }}</td>
                  <td>{{ detail.end_day }}</td>
                  <td>{{ detail.official_days > 0 ? detail.days + ' (Official days in Labour Act: ' + detail.official_days + ')' : detail.days }}</td>
                  <td>{{ detail.maximum_carry_forward_days }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  export default {
    components: {
      SetupLeavesLeaveEntitlementSettingDialogCreate: () => import('@/components/hris/setup/leaves/leave-entitlement-setting/DialogCreate'),
      SetupLeavesLeaveEntitlementSettingDialogDelete: () => import('@/components/hris/setup/leaves/leave-entitlement-setting/DialogDelete'),
      SetupLeavesLeaveEntitlementSettingDialogEdit: () => import('@/components/hris/setup/leaves/leave-entitlement-setting/DialogEdit'),
      SetupLeavesLeaveEntitlementSettingDialogRestore: () => import('@/components/hris/setup/leaves/leave-entitlement-setting/DialogRestore'),
    },
    data () {
      return {
        headers: [
          {
            text: 'Leave Entitlement Settings Name',
            align: 'left',
            value: 'name',
          },
          {
            text: 'Leave Type',
            align: 'left',
            value: 'leave_type.name',
          },
          {
            text: '',
            sortable: false,
            value: 'action',
          },
        ],
        items: [
          { state: 'Active', value: true },
          { state: 'Deleted', value: false },
        ],
        detailHeaders: [
          {
            text: 'Start Day',
            value: 'start_day',
            sortable: false,
            align: 'center',
          },
          {
            text: 'End Day',
            value: 'end_day',
            sortable: false,
            align: 'center',
          },
          {
            text: 'Days',
            value: 'days',
            sortable: false,
            align: 'center',
          },
          {
            text: 'Official Days',
            value: 'official_days',
            sortable: false,
            align: 'center',
          },
          {
            text: 'Maximum Carry Forward Days',
            value: 'maximum_carry_forward_days',
            sortable: false,
            align: 'center',
          },
        ],
        search: '',
        statusFilter: { state: 'Active', value: true },
        leaveEntitlementSettings: [],
        trashedLeaveEntitlementSettings: [],
        tableLoading: true,
      }
    },
    computed: {
      filteredLeaveEntitlementSettings () {
        return this.statusFilter ? this.leaveEntitlementSettings : this.trashedLeaveEntitlementSettings
      },
    },
    created () {
      this.loadData()
    },
    methods: {
      loadData () {
        Promise.all([
          this.$api.leaveEntitlementSettings().list(),
          this.$api.leaveEntitlementSettings().listTrashed(),
        ]).then(([leaveEntitlementSettings, trashedLeaveEntitlementSettings]) => {
          this.leaveEntitlementSettings = leaveEntitlementSettings.data
          this.trashedLeaveEntitlementSettings = trashedLeaveEntitlementSettings.data

          this.tableLoading = false
        })
      },
    },
  }
</script>

<style scoped>
.details {
  padding-left:85px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding-right:85px;
}

.remove-bottom-border {
  border-bottom: 0px !important
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important
}
</style>
